import React, { useState, useEffect } from 'react';
import { Calendar, Check, Clock, Globe, Lock, RefreshCw, Settings, Shield, X, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface CalendarProvider {
  id: string;
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  lastSync?: Date;
  calendars?: string[];
}

interface SyncSettings {
  frequency: 'realtime' | 'hourly' | 'daily';
  selectedCalendars: string[];
  eventVisibility: 'public' | 'private';
  twoWaySync: boolean;
}

const CalendarIntegration: React.FC = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<CalendarProvider[]>([
    {
      id: 'google',
      name: 'Google Calendar',
      icon: <Calendar className="h-6 w-6 text-red-500" />,
      connected: false,
      calendars: []
    },
    {
      id: 'apple',
      name: 'Apple Calendar',
      icon: <Calendar className="h-6 w-6 text-gray-900" />,
      connected: false,
      calendars: []
    },
    {
      id: 'outlook',
      name: 'Microsoft Outlook',
      icon: <Calendar className="h-6 w-6 text-blue-500" />,
      connected: false,
      calendars: []
    },
    {
      id: 'yahoo',
      name: 'Yahoo Calendar',
      icon: <Calendar className="h-6 w-6 text-purple-500" />,
      connected: false,
      calendars: []
    }
  ]);

  const [syncSettings, setSyncSettings] = useState<SyncSettings>({
    frequency: 'hourly',
    selectedCalendars: [],
    eventVisibility: 'private',
    twoWaySync: false
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // Load saved credentials on component mount
  useEffect(() => {
    if (user) {
      loadCalendarCredentials();
    }
  }, [user]);

  const loadCalendarCredentials = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user.id)
        .in('provider', ['google_calendar', 'apple_calendar', 'outlook_calendar', 'yahoo_calendar']);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Update providers with connection status
        const updatedProviders = [...providers];
        
        data.forEach(credential => {
          const providerIndex = updatedProviders.findIndex(p => 
            p.id === credential.provider.replace('_calendar', '')
          );
          
          if (providerIndex !== -1) {
            updatedProviders[providerIndex] = {
              ...updatedProviders[providerIndex],
              connected: true,
              lastSync: new Date(),
              calendars: credential.credentials.calendars || ['Primary Calendar']
            };
          }
        });
        
        setProviders(updatedProviders);
        
        // Update sync settings with selected calendars
        const allCalendars = updatedProviders
          .filter(p => p.connected)
          .flatMap(p => p.calendars || []);
        
        setSyncSettings(prev => ({
          ...prev,
          selectedCalendars: allCalendars
        }));
      }
    } catch (error) {
      console.error('Error loading calendar credentials:', error);
      toast.error('Failed to load calendar connections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (providerId: string) => {
    if (!user) {
      toast.error('You must be logged in to connect calendars');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would initiate OAuth flow
      // For this demo, we'll simulate a successful connection
      
      // Simulate OAuth flow delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock calendars that would be returned from the API
      const mockCalendars = ['Primary Calendar', 'Work', 'Personal'];
      
      // Store the credentials in the database
      const { error } = await supabase
        .from('user_credentials')
        .upsert({
          user_id: user.id,
          provider: `${providerId}_calendar`,
          credentials: {
            access_token: 'mock_access_token',
            refresh_token: 'mock_refresh_token',
            expiry: new Date(Date.now() + 3600000).toISOString(),
            calendars: mockCalendars
          }
        });
      
      if (error) throw error;
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: true,
            lastSync: new Date(),
            calendars: mockCalendars
          };
        }
        return provider;
      }));
      
      // Update sync settings with the new calendars
      setSyncSettings(prev => ({
        ...prev,
        selectedCalendars: [...prev.selectedCalendars, ...mockCalendars]
      }));
      
      toast.success(`Connected to ${providerId} successfully`);
    } catch (error) {
      console.error(`Failed to connect to ${providerId}:`, error);
      toast.error(`Failed to connect to ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (providerId: string) => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // Delete the credentials from the database
      const { error } = await supabase
        .from('user_credentials')
        .delete()
        .eq('user_id', user.id)
        .eq('provider', `${providerId}_calendar`);
      
      if (error) throw error;
      
      // Get the calendars that will be removed
      const providerToRemove = providers.find(p => p.id === providerId);
      const calendarsToRemove = providerToRemove?.calendars || [];
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: false,
            lastSync: undefined,
            calendars: []
          };
        }
        return provider;
      }));
      
      // Remove the calendars from selected calendars
      setSyncSettings(prev => ({
        ...prev,
        selectedCalendars: prev.selectedCalendars.filter(cal => 
          !calendarsToRemove.includes(cal)
        )
      }));
      
      toast.success(`Disconnected from ${providerId}`);
    } catch (error) {
      console.error(`Failed to disconnect from ${providerId}:`, error);
      toast.error(`Failed to disconnect from ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (providerId: string) => {
    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider?.connected) return;

      toast.promise(
        new Promise(resolve => setTimeout(resolve, 2000)),
        {
          loading: 'Syncing calendars...',
          success: 'Calendars synced successfully',
          error: 'Failed to sync calendars'
        }
      );

      setProviders(prev => prev.map(p => {
        if (p.id === providerId) {
          return { ...p, lastSync: new Date() };
        }
        return p;
      }));
    } catch (error) {
      console.error('Sync error:', error);
    }
  };

  const handleCalendarToggle = (calendar: string) => {
    setSyncSettings(prev => ({
      ...prev,
      selectedCalendars: prev.selectedCalendars.includes(calendar)
        ? prev.selectedCalendars.filter(c => c !== calendar)
        : [...prev.selectedCalendars, calendar]
    }));
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would update settings in the database
      // For this demo, we'll just show a success message
      
      toast.success('Calendar settings saved successfully');
    } catch (error) {
      console.error('Error saving calendar settings:', error);
      toast.error('Failed to save calendar settings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Calendar Integration</h2>
          <p className="mt-1 text-sm text-gray-500">
            Connect and sync your calendars to manage all your appointments in one place
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={syncSettings.frequency}
            onChange={(e) => setSyncSettings(prev => ({
              ...prev,
              frequency: e.target.value as SyncSettings['frequency']
            }))}
            className="rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
          >
            <option value="realtime">Real-time sync</option>
            <option value="hourly">Hourly sync</option>
            <option value="daily">Daily sync</option>
          </select>
          
          <button
            onClick={handleSaveSettings}
            className="btn btn-primary py-1.5 px-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>

      {/* Calendar Providers */}
      <div className="grid grid-cols-1 gap-6">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className={`bg-white rounded-lg border p-6 ${
              provider.connected ? 'border-primary' : 'border-gray-200'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {provider.icon}
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
                  {provider.connected && provider.lastSync && (
                    <p className="text-sm text-gray-500">
                      Last synced: {provider.lastSync.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {provider.connected ? (
                  <>
                    <button
                      onClick={() => handleSync(provider.id)}
                      className="p-2 text-gray-400 hover:text-gray-500"
                      title="Sync now"
                    >
                      <RefreshCw className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDisconnect(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(provider.id)}
                    className="btn btn-primary py-1.5 px-3"
                    disabled={isLoading}
                  >
                    {isLoading && provider.id === 'google' ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>

            {provider.connected && provider.calendars && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Calendars</h4>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={syncSettings.twoWaySync}
                        onChange={(e) => setSyncSettings(prev => ({
                          ...prev,
                          twoWaySync: e.target.checked
                        }))}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Two-way sync</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {provider.calendars.map((calendar) => (
                    <label
                      key={calendar}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={syncSettings.selectedCalendars.includes(calendar)}
                          onChange={() => handleCalendarToggle(calendar)}
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm font-medium text-gray-700">{calendar}</span>
                      </div>
                      <select
                        className="text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        value={syncSettings.eventVisibility}
                        onChange={(e) => setSyncSettings(prev => ({
                          ...prev,
                          eventVisibility: e.target.value as 'public' | 'private'
                        }))}
                      >
                        <option value="private">Private</option>
                        <option value="public">Public</option>
                      </select>
                    </label>
                  ))}
                </div>

                {/* Privacy Settings */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Privacy Settings
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Encrypt calendar data</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Hide attendee details</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={false}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Share availability only</span>
                    </label>
                  </div>
                </div>
                
                {/* Chatbot Integration */}
                <div className="mt-6 bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-500" />
                    Chatbot Integration
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Allow the chatbot to check your calendar availability and schedule appointments for you.
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Enable appointment scheduling</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Send email confirmations</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Add buffer time between meetings</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarIntegration;